import { useCallback, useEffect, useRef, useState } from 'react';
import { healthsherpa } from './api';

// =============================================================================
// HealthSherpa page text size (5.31.0, Henry 2026-10-29). ONE root value: the CSS variable --hs-scale on the page's
// root element (HealthSherpa.jsx). Every HealthSherpa font size is fs(px) = calc(<px>px * var(--hs-scale, 1)), so it
// scales with that one value while dragging (no re-render of the page), and anything rendered OUTSIDE the page root —
// the rest of InsureDocs, the Writer drawer, the InsureDocs client view's Health coverage panel (which reuses
// HealthSherpaPerson) — falls back to 1 and is unchanged.
// Saved PER USER on the server (GET/PUT /api/healthsherpa/work/text-size -> user_settings 'hs.text_scale'), so it
// follows the login to any computer. A per-user localStorage copy only avoids a flash on load; the server value wins.
// =============================================================================

export const SCALE_MIN = 80;
export const SCALE_MAX = 130;
export const SCALE_STEP = 5;
export const SCALE_DEFAULT = 100;
export const SAVE_DELAY_MS = 400;

export const fs = (px) => `calc(${px}px * var(--hs-scale, 1))`;
export const clampScale = (v) => {
  const n = Math.round(Number(v) / SCALE_STEP) * SCALE_STEP;
  return Number.isFinite(n) ? Math.min(SCALE_MAX, Math.max(SCALE_MIN, n)) : SCALE_DEFAULT;
};
export const cacheKey = (userId) => `insuredocs.hs.textScale.v1.u${userId || 'anon'}`;

function readCache(userId) {
  try {
    const v = Number(window.localStorage.getItem(cacheKey(userId)));
    return v ? clampScale(v) : SCALE_DEFAULT;
  } catch (_e) { return SCALE_DEFAULT; }
}
function writeCache(userId, v) {
  try { window.localStorage.setItem(cacheKey(userId), String(v)); } catch (_e) { /* storage unavailable */ }
}

// -> [percent, setPercent]; setPercent applies at once and saves to the server after SAVE_DELAY_MS of no change.
export function useHsTextScale(userId) {
  const [pct, setPct] = useState(() => readCache(userId));
  const touched = useRef(false);
  const timer = useRef(null);
  useEffect(() => {
    let alive = true;
    healthsherpa.work.textSize().then((r) => {
      if (!alive || touched.current) return;               // the user already moved the slider: keep theirs
      const v = clampScale(r.data.percent);
      setPct(v); writeCache(userId, v);
    }).catch(() => { /* offline / old server: keep the cached value */ });
    return () => { alive = false; };
  }, [userId]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const set = useCallback((v) => {
    const n = clampScale(v);
    touched.current = true;
    setPct(n); writeCache(userId, n);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { healthsherpa.work.saveTextSize(n).catch(() => { /* kept locally; next change retries */ }); }, SAVE_DELAY_MS);
  }, [userId]);
  return [pct, set];
}

// The top-bar control: "Text size" slider + the percent, which is also the reset-to-100% button.
export function TextSizeControl({ value, onChange, c }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: fs(12), color: c.textSecondary, whiteSpace: 'nowrap' }}
      data-testid="hs-scale">
      <label htmlFor="hs-scale-range">Text size</label>
      <input id="hs-scale-range" type="range" min={SCALE_MIN} max={SCALE_MAX} step={SCALE_STEP} value={value}
        onChange={(e) => onChange(Number(e.target.value))} aria-valuetext={`${value}%`} data-testid="hs-scale-range"
        style={{ width: 110, cursor: 'pointer' }} />
      <button type="button" onClick={() => onChange(SCALE_DEFAULT)} disabled={value === SCALE_DEFAULT} data-testid="hs-scale-reset"
        title={value === SCALE_DEFAULT ? 'Text size 100%' : 'Reset to 100%'}
        style={{ minWidth: '4.2em', padding: '4px 6px', fontSize: fs(12), borderRadius: 5, cursor: value === SCALE_DEFAULT ? 'default' : 'pointer',
          border: `1px solid ${c.borderColor}`, background: c.panelBg, color: c.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
        {value}%{value === SCALE_DEFAULT ? '' : ' ↺'}
      </button>
    </span>
  );
}

// Form controls don't inherit font-size; inside the page root they get the browser's default size times the scale
// (zero specificity — any inline style or class rule still wins).
export const ROOT_CSS = ':where(.hs-root) :is(input, select, textarea, button) { font-size: calc(13.333px * var(--hs-scale, 1)); }';

